import { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ChevronRight, Clock, CalendarDays } from "lucide-react";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { Reveal } from "./Reveal";
import { ARTICLES, DOMAIN } from "../data/articles";
import { useLang } from "../i18n";

const DEFAULT_TITLE = "Impresa Edile a Bologna | Adriatica S.r.l. Semplificata";
const DEFAULT_DESC =
  "Adriatica S.r.l. Semplificata: impresa edile a Bologna con oltre 20 anni di esperienza. Ristrutturazioni, nuove costruzioni, impianti e lavori chiavi in mano.";

export default function ArticlePage() {
  const { slug } = useParams();
  const { t } = useLang();
  const a = ARTICLES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!a) return;
    document.title = a.metaTitle;
    const md = document.querySelector('meta[name="description"]');
    if (md) md.setAttribute("content", a.metaDescription);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `${DOMAIN}/guide/${slug}`);
    return () => {
      document.title = DEFAULT_TITLE;
      if (md) md.setAttribute("content", DEFAULT_DESC);
      if (canonical) canonical.setAttribute("href", `${DOMAIN}/`);
    };
  }, [slug, a]);

  if (!a) return <Navigate to="/guide" replace />;

  return (
    <div className="grain bg-[#070D18] min-h-screen" data-testid={`article-page-${slug}`}>
      <Navbar />
      <main>
        <section className="relative pt-40 pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img src={a.heroImg} alt={a.heroAlt} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#070D18] via-[#070D18]/88 to-[#070D18]/45" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070D18] via-transparent to-[#070D18]/70" />
          </div>
          <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
            <nav className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-400" data-testid="article-breadcrumb">
              <a href="/" className="hover:text-[#F3E08B] transition-colors">Home</a>
              <ChevronRight size={12} className="text-[#D4AF37]" />
              <a href="/guide" className="hover:text-[#F3E08B] transition-colors">{t("Guide", "Guides")}</a>
              <ChevronRight size={12} className="text-[#D4AF37]" />
              <span className="text-[#F3E08B]">{a.category}</span>
            </nav>
            <h1 className="mt-6 font-display font-bold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
              {a.title}
            </h1>
            <div className="mt-5 flex items-center gap-5 text-xs text-slate-400">
              <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} className="text-[#D4AF37]" />{a.date}</span>
              <span className="inline-flex items-center gap-1.5"><Clock size={13} className="text-[#D4AF37]" />{t("Lettura", "Read time")}: {a.readTime}</span>
            </div>
          </div>
        </section>

        <article className="relative pb-10">
          <div className="max-w-4xl mx-auto px-6 lg:px-10">
            {a.sections.map((sec, i) => (
              <Reveal key={sec.h} delay={0.03 * i} className="mt-12 first:mt-0">
                <h2 className="font-serif text-2xl sm:text-3xl font-medium text-white">{sec.h}</h2>
                {sec.paragraphs.map((p, j) => (
                  <p key={j} className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">
                    {p}
                  </p>
                ))}
              </Reveal>
            ))}

            <Reveal className="mt-14">
              <div
                data-testid="article-cta-box"
                className="rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/15 via-[#D4AF37]/5 to-transparent p-7 sm:p-9 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
              >
                <div>
                  <h3 className="font-serif text-2xl font-medium text-white">{t("Hai un progetto in mente?", "Have a project in mind?")}</h3>
                  <p className="mt-2 text-sm sm:text-base text-slate-300 max-w-md">
                    {t(
                      "Sopralluogo e preventivo gratuiti a Bologna e provincia. Raccontaci la tua idea.",
                      "Free site visit and quote in Bologna and its province. Tell us your idea."
                    )}
                  </p>
                </div>
                <a
                  href="/#contatti"
                  data-testid="article-cta-quote"
                  className="group inline-flex items-center gap-2 text-sm font-semibold text-[#070D18] bg-gradient-to-r from-[#D4AF37] to-[#F3E08B] px-6 py-3.5 rounded-full hover:shadow-[0_0_28px_rgba(212,175,55,0.45)] transition-shadow duration-300 shrink-0"
                >
                  {t("Richiedi un preventivo", "Request a quote")}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </Reveal>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
